import moment from 'moment-timezone';

import { task } from 'ember-concurrency';

import { action, get, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { clearAttribute } from 'beebee-base/lib/utility';
import { createCustomFieldForOcf } from 'beebee-base/lib/utility/models';
import { beebeeAjaxPost, beebeeAjaxGet } from 'beebee-base/lib/utility/ajax';

import { later } from '@ember/runloop';

import { inject as controller } from '@ember/controller';
import { sanitizeHtml } from 'beebee-helper/helpers/safe-string';

import { debug } from '@ember/debug';

import getConfig from 'beebee-base/lib/get-config';

import CheckoutContactController from 'e-commerce/lib/checkout-contact-controller';
import {
  checkRequiredFiscalFields,
  completaStraniero as completaStranieroFor,
} from 'e-commerce/lib/fiscal-contact-validation';
import { classifyConfirmError, confirmFailureNotice, safeJson } from 'e-commerce/lib/confirm-error';
import {
  addPartecipanteObj as addPartecipantiTo,
  buildAddressObj,
  buildCheckoutDataObj,
  castSafeEvents,
  compattaProdotti as mergeIndividualProducts,
  pushPartecipanti as flattenPartecipanti,
} from 'e-commerce/lib/checkout-payload';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      controller — multi-step checkout wizard (contact form → participants →
 *              summary/riepilogo → payment), fiscal address/CF completion for foreign
 *              contacts, order confirmation against /ecommerce_confirm.
 *   extends:   CheckoutContactController (lib/checkout-contact-controller — buyer
 *              contact resolution + refresh, see that file's MAP)
 *   template:  none
 *   qp:        -
 *   key:       checkoutTask (submits /ecommerce_confirm, routes to zero-price or payment
 *              flow), confirm1stepTask (saves contact, advances step), savePartecipantiTask
 *              (validates participants, builds riepilogo), createRiepilogo
 *              (/ecommerce_freeprice), confermaPrezzo0 (/ecommerce_zero), requestPaymentTask,
 *              loadPrenoTask, _applyConfirmFailure (the single place that decides what a
 *              failed order confirmation tells the customer and whether the cart dies),
 *              acknowledgeUnknownOutcome (clears the duplicate-order gate).
 *              The buyer-contact half (contact getter/setter,
 *              refreshContactTask, isAlbergo) lives in the base class; checkRequired/
 *              completaStraniero and createDataObj/createAddressObj/addPartecipanteObj/
 *              pushPartecipanti/compattaProdotti/_castSafeEvents are thin wrappers over
 *              lib/fiscal-contact-validation and lib/checkout-payload.
 *   uses:      applicationController (@controller('application') — cart, emptyCart,
 *              saveRecordRelated, gdprField), lib/checkout-contact-controller,
 *              lib/fiscal-contact-validation (shared with payment-request.js),
 *              lib/checkout-payload (buildAddressObj shared with checkin.js),
 *              lib/confirm-error (classifyConfirmError — the /ecommerce_confirm
 *              failure taxonomy read in checkoutTask's catch),
 *              beebee-helper safe-string sanitizeHtml, lib/utility/ajax, lib/get-config
 *              (tagliandoPdfUrl -> API scuolasci_tagliando_export_pdf, POP-568)
 *   siblings:  routes/checkout.js (setupController resets step/payment/promo residuals
 *              on entry), controllers/application.js (cart owner)
 *   tests:     checkout-getters-test.js, checkout-order-test.js,
 *              checkout-request-payment-test.js, checkout-tasks-test.js,
 *              checkout-consent-gate-test.js, checkout-summary-reads-test.js,
 *              checkout-write-test.js
 *   gotchas:   `canBuy` reads GDPR consent rows via `get(d, 'active')` not `d.active` —
 *              the checkboxes write through Glimmer `mut` (= `set()`), so a native
 *              property read never revalidates the getter's tracked dependency.
 *              checkoutTask empties the cart on exactly ONE outcome — a 422 whose
 *              message is the backend's "products no longer available" (POP-349,
 *              see lib/confirm-error). Every other failure keeps the cart: it is a
 *              per-tab sessionStorage object with no server copy, so emptying it on a
 *              timeout or a 500 deletes the customer's basket for good. An outcome we
 *              never learned (no answer / 5xx / 2xx without a payment id) sets
 *              `confirmOutcomeUnknown`, which disables the buy button until the
 *              customer explicitly acknowledges the possible duplicate. Once
 *              /ecommerce_confirm answers 200 the order exists, so later failures must
 *              not empty the cart or claim products are unavailable. clearItems/delCart
 *              MUST stay @action — passed unbound to <PaymentMethod> as this.args.clearItems().
 *              The `contact` getter must stay side-effect free (EMBER-SCUOLE-ECOMMERCE-2YQ):
 *              that reasoning, and the refresh itself, now live in
 *              lib/checkout-contact-controller — read its comments before touching the
 *              buyer contact.
 *
 * Owns the checkout wizard's step state and the two fiscal-completion/save paths
 * (contact, participants) that feed the final order confirmation call.
 */
export default class CheckoutController extends CheckoutContactController {
  @controller('application') applicationController;

  @tracked step = 1;
  @tracked riepilogo = null;
  @tracked notePrenotazione = null;

  @tracked termAccepted = false;
  @tracked checkTerms = false;

  @tracked customFieldsRequired;
  @tracked paymentAPI;
  @tracked paymentZero;
  @tracked eventsReloaded;
  @tracked voucherCode;
  @tracked showPromoCode;

  /**
   * Set when /ecommerce_confirm left us without an answer (no response, a
   * timeout, a 5xx, or a 2xx with no payment id): the order MAY have been
   * placed. While it is true the buy button is disabled and `checkoutTask`
   * refuses to post, so the customer cannot book and pay a second time by
   * clicking again; `acknowledgeUnknownOutcome` is the only way out.
   *
   * Deliberately NOT reset by routes/checkout.js `setupController`: leaving and
   * re-entering the checkout does not make the possible duplicate go away.
   */
  @tracked confirmOutcomeUnknown = false;

  get dataUrl() {
    return getConfig(this).APP.urlData;
  }

  // POP-568: ticket PDF for the hotel user, served by the API, which picks the
  // engine per tenant and resolves the organization from the host. Arrow field so
  // the template can call it as a plain-function helper with `this` bound.
  tagliandoPdfUrl = (projectId, partecipanteId) => {
    const qs = new URLSearchParams({ project_id: projectId ?? '' });
    if (partecipanteId) qs.set('partecipante_id', partecipanteId);
    qs.set('completo', 'true');
    qs.set('hotel', 'true');
    qs.set('hotel_name', this.session.me?.label ?? '');
    qs.set('access_token', this.session.token ?? '');
    return `${getConfig(this).APP.urlApi}/scuolasci_tagliando_export_pdf?${qs}`;
  };

  get not_required_field() {
    const optionField = this.session.option_field;
    return optionField?.ecommerce_not_required_field;
  }

  get safeCustomNotes() {
    const value = this.session.option_field?.ecommerce_custom_notes;
    return sanitizeHtml(value);
  }

  get contactStateAltri() {
    return this.session?._snapshotFindById?.('contact_states', '114') ?? null;
  }

  get productStateIndividuale() {
    return this.store.peekRecord('product_state', 92);
  }

  get showPartecipanti() {
    const applicationController = this.applicationController || {};
    const { ecommerceLoginBefore, cart } = applicationController;
    return !(ecommerceLoginBefore && cart?.private?.length === 0);
  }

  /*ecommerce_partecipanti_field: computed(
    'session.option_field.ecommerce_partecipanti_field',
    function () {
        return this.session.option_field?.ecommerce_partecipanti_field;
    }
  ).readOnly(),

  isCFRequired: computed(
    'ecommerce_partecipanti_field.cod_fiscale',
    function () {
        return this.ecommerce_partecipanti_field?.ecommerce_partecipanti_field?.cod_fiscale;
    }
  ).readOnly(),

  isMailRequired: computed('ecommerce_partecipanti_field.mail', function () {
    return this.ecommerce_partecipanti_field?.mail;
  }).readOnly(),*/

  get customFieldsToShow() {
    if (!this.session.me?.id) {
      return false;
    }

    const contact = this.store.peekRecord('contact', this.session.me?.id);

    if (
      Array.isArray(this.session.option_field?.custom_field_parameter) &&
      contact &&
      contact.get('organization.organization_custom_fields')
    ) {
      const ocfId = [];

      for (let i = 0; i < this.session.option_field.custom_field_parameter.length; i++) {
        const c = this.session.option_field.custom_field_parameter[i];

        if (c['ecommerce'] === true) {
          if (!c['role_id'] || c['role_id'] === contact.get('role.id')) {
            // An id configured in option_field but missing from the store yields a
            // null OCF, i.e. a field that renders empty and cannot carry an answer.
            const cf = createCustomFieldForOcf(this.store, c['id']);
            if (cf) {
              set(cf, 'required', c.required_ecom);
              ocfId.push(cf);
            }
          }
        }
      }

      return ocfId;
    }

    return null;
  }

  get ecommerce_collettive_single() {
    return this.session.option_field?.ecommerce_collettive_single;
  }

  get generalTerms() {
    return this.session.option_field?.ecommerce_general_term;
  }

  get defMail() {
    return this.session.option_field?.default_mail;
  }

  get compDefault() {
    return this.session.option_field?.default_comp;
  }

  get canBuy() {
    const { generalTerms, termAccepted } = this;

    if (generalTerms) {
      if (termAccepted) {
        let requiredActive = true;

        const organizationTermOptions =
          this.applicationController?.gdprField?.organization_term_options?.data;

        if (Array.isArray(organizationTermOptions)) {
          organizationTermOptions.forEach(function (d) {
            // `active` MUST be read through `get`. The consent rows are plain
            // objects parsed out of `/organizations/open`; the checkbox writes
            // them with `(mut c.active)`, i.e. through Glimmer's `mut`
            // reference, which calls `set()` and so dirties
            // `tagFor(row, 'active')`. A native `d.active` read consumes no
            // tag, so this getter kept whatever value it had when
            // `termAccepted` (the only @tracked input) last changed: ticking
            // the required consent AFTER the general terms left the pay button
            // permanently disabled, and un-ticking it left the button enabled.
            // `checkin.js` still carries the pre-Octane
            // `@computed('…@each.{required,registration,active}')` that made
            // this work; the controller migration dropped it here.
            if (get(d, 'required') && get(d, 'registration') && !get(d, 'active')) {
              requiredActive = false;
            }
          });
        }

        return requiredActive;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

  checkRequired(showError = true) {
    return checkRequiredFiscalFields(this.contact, {
      notRequired: this.not_required_field,
      showError,
    });
  }

  completaStraniero() {
    completaStranieroFor(this.contact);
  }

  compattaProdotti(products) {
    return mergeIndividualProducts(products);
  }

  createAddressObj() {
    return buildAddressObj(this.contact?.get('addresses')[0]);
  }

  @task({ drop: true }) *checkoutTask() {
    const { session, store, applicationController, intl, riepilogo, customFieldsToShow } = this;

    // A previous attempt ended without an answer: the order may already exist.
    // Re-posting silently would book and charge the customer twice, so the
    // retry has to come from `acknowledgeUnknownOutcome` (an explicit second
    // click on the callout in checkout.hbs), never from this button alone.
    // First statement of the task on purpose: everything below mutates the
    // riepilogo that would be sent (notes, ref, custom fields).
    if (this.confirmOutcomeUnknown) {
      this.notify.warning(intl.t('checkout.order outcome unknown'));

      return;
    }

    try {
      var cookie = document.cookie.split(';').find((item) => item.includes('beebee_ref='));
      if (cookie) {
        riepilogo.data.ref = cookie.split('=')[1];
      }
    } catch (e) {
      debug(e);
    }

    let customFieldsRequired = false;
    set(this, 'customFieldsRequired', false);

    if (Array.isArray(customFieldsToShow)) {
      riepilogo.data.custom_fields = [];
      customFieldsToShow.forEach((customField) => {
        if (customField.required) {
          if (
            ['number', 'currency', 'range'].includes(
              customField.get('organization_custom_field.data_type')
            ) &&
            (!customField.number_value || customField.number_value === '')
          ) {
            customFieldsRequired = true;
          } else if (
            customField.get('organization_custom_field.data_type') === 'checkbox' &&
            (!customField.boolean_value || customField.boolean_value === '')
          ) {
            customFieldsRequired = true;
          } else if (!customField.value || customField.value === '') {
            customFieldsRequired = true;
          }
        }

        riepilogo.data.custom_fields.push({
          organization_custom_field_id: customField.get('organization_custom_field.id'),
          value: clearAttribute(customField.value),
          number_value: clearAttribute(customField.number_value),
          boolean_value: customField.boolean_value,
        });
      });
    }

    if (customFieldsRequired) {
      set(this, 'customFieldsRequired', true);

      this.notify.warning(intl.t('missing mandatory fields'));

      return;
    }

    riepilogo.data.notes = this.notePrenotazione;
    riepilogo.data.contact.gdpr = applicationController.get(
      'gdprField.organization_term_options.data'
    );

    try {
      var cookie = document.cookie.split(';').find((item) => item.includes('beebee_ref='));

      if (cookie) {
        riepilogo.data.notes = `${
          riepilogo.data.notes ? riepilogo.data.notes : ''
        } ref: ${cookie.split('=')[1]}`;
      }
    } catch (e) {
      debug(e);
    }

    // Only the /ecommerce_confirm call itself can genuinely report that the
    // products are gone: it is the backend that re-checks availability. Once it
    // has answered, the order EXISTS — so every later failure is a client-side
    // problem and must not tell the customer their products are unavailable,
    // and must not empty the cart (the only local record of what they bought).
    let data;

    try {
      data = yield beebeeAjaxPost(
        `${getConfig(this).APP.urlApi}/ecommerce_confirm`,
        session.token,
        riepilogo
      );
    } catch (e) {
      // POP-349. This catch used to answer EVERY rejection — 422, 400, 500,
      // expired token, dropped connection — with "products are no longer
      // available" and then `emptyCart()`. The cart is a sessionStorage-backed
      // per-tab object with no server-side copy, so that destroyed the
      // customer's basket over a failure that often had nothing to do with
      // availability, and told them something untrue about why. Sentry saw
      // nothing at all, which is why every report of "the cart is empty at
      // payment" arrived undiagnosable.
      const outcome = classifyConfirmError(e);

      // One Sentry event per failed confirmation, carrying everything needed to
      // place it: `log.error` routes an Error argument through captureException
      // in production and to the console in dev.
      this.log.error(
        'ecommerce_confirm failed',
        `status=${outcome.status}`,
        `kind=${outcome.kind}`,
        `body=${safeJson(e?.responseJSON)}`,
        e instanceof Error ? e : new Error(String(e))
      );

      this._applyConfirmFailure(outcome);

      return;
    }

    // `beebeeAjaxPost` resolves `null` on an empty 200/204 body (see
    // beebee-base/lib/utility/ajax): the backend answered 2xx, so the order and
    // the payment EXIST, but we have no id to continue the wizard with. That is
    // an unknown outcome, not a failure — retrying would book a second time —
    // and it must be caught here, before `data.payment_id` throws a TypeError
    // into the post-confirm catch below and gets reported as a plain error.
    if (!data?.payment_id) {
      this.log.error(
        'ecommerce_confirm answered 2xx without a payment id',
        `body=${safeJson(data)}`
      );
      this.confirmOutcomeUnknown = true;
      this.notify.warning(intl.t('checkout.order outcome unknown'));

      return;
    }

    try {
      if (typeof gtag === 'function') {
        gtag('event', 'purchase', {
          event_category: 'ecommerce',
          event_label: 'purchase',
          send_to: 'G-Q4R8QL4WC0',
        });
      }

      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            transaction_id: data.payment_id,
            value: riepilogo.data.prezzo_preno,
            items: [
              ...(riepilogo.data.groups || []).map((p) => ({
                item_id: p.id,
                item_name: p.name,
                item_category: 'groups',
                price: p.price,
                quantity: p.quantity || 1,
              })),
              ...(riepilogo.data.private || []).map((p) => ({
                item_id: p.id,
                item_name: p.name,
                item_category: 'private',
                price: p.price,
                quantity: p.quantity || 1,
              })),
              ...(riepilogo.data.voucher || []).map((p) => ({
                item_id: p.id,
                item_name: p.name,
                item_category: 'voucher',
                price: p.price,
                quantity: p.quantity || 1,
              })),
            ],
          },
        });
      }

      if (riepilogo.data.preno_acconto_totale === 0 || riepilogo.data.prezzo_preno === 0) {
        yield this.confermaPrezzo0.perform(data.payment_id);
      } else {
        const p = yield store.findRecord('payment', data.payment_id, {
          reload: true,
        });

        set(this, 'paymentAPI', p);
        set(this, 'step', 4);

        p.project_reloaded = [];
        const pProjects = yield p.projects;
        pProjects.forEach((project) => {
          this.loadPrenoTask.perform(p, project);
        });

        later(
          this,
          function () {
            const stepEl = document.getElementById('checkOutStep3');

            if (stepEl) {
              const rect = stepEl.getBoundingClientRect();
              const top = rect.top + window.scrollY;

              window.scrollTo({ top, behavior: 'smooth' });
            }
          },
          100
        );
      }
      // applicationController.scrollBottom();
    } catch (e) {
      debug(e);

      // The order is already placed. Report a generic failure and KEEP the
      // cart: emptying it here would destroy the customer's basket over a
      // problem that has nothing to do with product availability. It is also
      // why the customer must not be invited to buy again from here: the
      // booking exists, only the follow-up step failed.
      this.log.error('ecommerce_confirm post-order step failed', e);
      this.notify.warning(intl.t('Errors occurred while booking'));
    }
  }

  /**
   * Turns a classified /ecommerce_confirm failure into what the customer sees.
   * The decision itself is a pure function (`confirmFailureNotice`) so it can be
   * read and tested without a controller; this only applies it. The logging
   * stays at the call site, in `checkoutTask`'s catch.
   *
   * Kept a plain method (no yield inside) so it can run from that catch without
   * adding another cancellation point.
   */
  _applyConfirmFailure(outcome) {
    const notice = confirmFailureNotice(outcome);

    if (notice.blockRetry) this.confirmOutcomeUnknown = true;
    // The API's own words when it sent any — but they are Italian prose
    // (error400 bodies), so only an Italian storefront gets them verbatim;
    // every other locale reads our localized fallback. The raw detail is
    // logged by the caller regardless.
    const italian = String(this.intl.primaryLocale || '').toLowerCase().startsWith('it');
    this.notify.warning((italian && notice.detail) || this.intl.t(notice.messageKey));
    if (notice.emptyCart) this.applicationController.emptyCart();
  }

  /**
   * The customer says they checked (mail, bookings) and wants to order again
   * after an unknown outcome. Only this clears the gate: the buy button stays
   * disabled until it runs, so no retry can go out silently.
   */
  @action
  acknowledgeUnknownOutcome() {
    this.confirmOutcomeUnknown = false;
  }

  @task({ drop: true }) *loadPrenoTask(payment, project) {
    const { session, store } = this;

    set(this, 'eventsReloaded', []);
    try {
      const fullData = yield beebeeAjaxGet(
        `${getConfig(this).APP.urlApi}/prenotazione/${project.id}`,
        session.token
      );

      store.pushPayload(fullData);
      // project_reloaded is an ad-hoc plain array rendered in checkout.hbs: reassign via set() to keep reactivity
      set(payment, 'project_reloaded', [
        ...(payment.project_reloaded || []),
        store.peekRecord('project', project.id),
      ]);
    } catch (e) {
      // Gestisci l'errore qui
    }
  }

  @task({ drop: true }) *confirm1stepTask() {
    const checkField = this.checkRequired();

    if (checkField) {
      const item = yield this.applicationController.saveRecordRelated(this.contact);
      if (!item) return;

      //this.store.pushPayload(item[0]);
      const storeItem = this.store.peekRecord('contact', item[0]?.data?.id);

      // The addresses cleanup below used to run BEFORE this guard: a save that
      // came back without a usable id crashed on `null.addresses` instead of
      // reporting, right after the contact was saved.
      if (!storeItem) {
        this.log.error('checkout confirm1stepTask: saved contact not in store', item);
        this.notify.error(this.intl.t('comunication error'));
        return;
      }

      const storeAddresses = yield storeItem.addresses;
      storeAddresses
        .filter((item) => !item.id)
        .forEach((item) => {
          const _i = storeAddresses.indexOf(item);
          if (_i !== -1) storeAddresses.splice(_i, 1);
        });

      set(this, 'contact', storeItem);
      if (this.applicationController.ecommerceLoginBefore) {
        // Same unconsumed-rejection class as 2YQ: this hasMany fetches contacts
        // the API refuses with a 404 (POP-151/POP-186 ownership gate), and the
        // task is performed unconsumed from the template. Stop instead of
        // walking on with an undefined participant — in this branch the group
        // participants ARE the related contact.
        let related_contacts;
        try {
          related_contacts = yield storeItem.related_contacts;
        } catch (e) {
          this.log.error('checkout confirm1stepTask: related contacts load failed', e);
          this.notify.error(this.intl.t('comunication error'));
          return;
        }

        const firstContactAltri = related_contacts.find(
          (i) => i.get('contact_state.id') === this.contactStateAltri?.id
        );

        for (let i = 0; i < this.applicationController.cart.groups.length; i++) {
          this.applicationController.cart.groups[i].partecipanti = [
            {
              level: firstContactAltri?.color,
              contact: firstContactAltri,
            },
          ];
        }

        if (this.applicationController.cart.private.length > 0) {
          set(this, 'step', 2);
        } else {
          yield this.createRiepilogo.perform();
        }
      } else {
        set(this, 'step', 2);
      }
      //this.applicationController.scrollBottom();
    } else {
      this.notify.warning(this.intl.t('missing mandatory fields'));
    }
  }

  @task({ drop: true }) *requestPaymentTask() {
    const { session, applicationController, intl, contact, paymentAPI, riepilogo } = this;
    const projects = [];
    let selectedContact = null;

    // Both relationship reads fetch what the store does not hold — `contacts`
    // one `GET /contacts/:id` per member, ids the API refuses with a 404 for a
    // client role (POP-151/POP-186). They used to sit outside any try, so the
    // rejection of this unconsumed task reached Sentry (2YQ class) while the
    // "send the payment request" button just did nothing.
    let paymentProjects;
    let paymentContacts;
    try {
      paymentProjects = yield paymentAPI.projects;
      paymentContacts = yield paymentAPI.contacts;
    } catch (e) {
      this.log.error('checkout requestPaymentTask: payment graph load failed', e);
      this.notify.error(intl.t('beebeeboard error'));
      return;
    }

    paymentProjects.forEach(function (pr) {
      projects.push(pr.id);
    });

    paymentContacts.forEach((ct) => {
      if (ct.id !== this.session.me?.id) {
        selectedContact = ct;
      }
    });

    let amount = riepilogo.data.prezzo_preno;
    if (
      riepilogo.data.preno_acconto_totale != null &&
      !isNaN(parseFloat(riepilogo.data.preno_acconto_totale))
    ) {
      amount = parseFloat(riepilogo.data.preno_acconto_totale);
    }

    try {
      yield beebeeAjaxPost(`${getConfig(this).APP.urlApi}/request_payment`, session.token, {
        url: `https://${this.session.me?.organization?.organization}.beebeeboard.com/scuolesci_ecommerce/payment-request?`,
        organization_name: this.session.me?.organization?.name,
        object: 'Richiesta pagamento',
        message: 'Richiesta pagamento',
        contact_name: selectedContact ? selectedContact.get('name') : null,
        contact_id: selectedContact ? selectedContact.get('id') : null,
        amount: amount,
        mail: contact.mail,
        thumbnail_url: this.session.me?.organization?.thumbnail_url,
        primary_color: this.session.me?.organization?.primary_color,
        projects: projects,
      });

      this.notify.success(intl.t('message sent'));
    } catch (e) {
      this.notify.error(intl.t('beebeeboard error'));
    }
  }

  @action
  emptyCart() {
    this.applicationController.emptyCart();
  }

  @action
  delCart(category = 'groups', product) {
    this.applicationController.delCart(category, product, this.applicationController);
  }

  isStepVisibleAndFilled(step) {
    const el = document.getElementById(`checkOutStep${step}`);

    if (!el) {
      return false;
    }

    return el.offsetParent !== null && el.innerText !== '';
  }

  @action
  backStep() {
    let step = this.step - 1;

    if (this.step > 1) {
      if (!this.isStepVisibleAndFilled(step - 1)) {
        step -= 1;
      }

      if (!this.isStepVisibleAndFilled(step - 1)) {
        step -= 1;
      }
    }

    if (step < 1) {
      step = 1;
    }

    set(this, 'step', step);
  }

  @task({ drop: true }) *savePartecipantiTask() {
    const { applicationController, intl } = this;
    let checkField = true;

    const ecommerce_select_level = applicationController.ecommerce_select_level;
    const all = applicationController.cart.private.concat(applicationController.cart.groups);

    all.forEach((c) => {
      if (c.partecipanti) {
        const fieldReset = [
          'notSelectError',
          'nameError',
          'ragSocialeError',
          'cfError',
          'mailError',
          'birthDateError',
          'levelError',
        ];

        c.partecipanti.forEach((p) => {
          for (let i = 0; i < fieldReset.length; i++) {
            set(p, fieldReset[i], false);
          }

          if (p.contact) {
            if (p.contact.get('fisica_giuridica')) {
              if (!p.contact.get('ragione_sociale')) {
                set(p, 'ragSocialeError', true);
                checkField = false;
              }
            } else {
              if (!p.contact.get('first_name')) {
                set(p, 'nameError', true);
                checkField = false;
              }
              if (!p.contact.get('last_name')) {
                set(p, 'nameError', true);
                checkField = false;
              }
            }

            if (!p.contact.get('data_nascita')) {
              set(p, 'birthDateError', true);
              checkField = false;
            }

            const min_age = c.item?.etminima;
            const max_age = c.item?.etmassima;

            if (
              min_age &&
              moment().diff(moment(p.contact.get('data_nascita')), 'years') < min_age
            ) {
              this.notify.warning(intl.t('Out of minimum age for this product'));
              checkField = false;
            }

            if (
              max_age &&
              moment().diff(moment(p.contact.get('data_nascita')), 'years') > max_age
            ) {
              this.notify.warning(intl.t('Out of maximum age for this product'));
              checkField = false;
            }

            if (ecommerce_select_level) {
              if (c.item.is_snow) {
                if (!p.contact.get('snow_color')) {
                  set(p, 'levelError', true);
                  checkField = false;
                }
              } else if (c.item.is_fondo) {
                if (!p.contact.get('fondo_color')) {
                  set(p, 'levelError', true);
                  checkField = false;
                }
              } else {
                if (!p.contact.get('color')) {
                  set(p, 'levelError', true);
                  checkField = false;
                }
              }
            }
          } else {
            set(p, 'notSelectError', true);
            checkField = false;
          }
        });
      } else {
        checkField = false;
      }

      if (Array.isArray(c.item?.modules_json?.moduli)) {
        c.item.modules_json.moduli.forEach((m) => {
          m.fields.forEach((f) => {
            set(f, 'fieldError', false);
            if (f.required && (!f.value || f.value === '' || f.value === ' ')) {
              set(f, 'fieldError', true);
              checkField = false;
            }
          });
        });
      }
    });

    if (checkField) {
      yield this.createRiepilogo.perform();
    } else {
      this.notify.warning(intl.t('missing mandatory fields'));
    }
  }

  @task({ drop: true }) *confermaPrezzo0(payment_id) {
    const { session, applicationController } = this;
    const me = session.me || {};

    try {
      yield beebeeAjaxPost(`${getConfig(this).APP.urlApi}/ecommerce_zero`, session.token, {
        payment_id,
        mail: me?.mail,
        nome: me?.name,
      });

      set(this, 'step', 4);
      set(this, 'paymentZero', true);
      applicationController.emptyCart();
    } catch (e) {
      // Left uncaught, a 4xx/network failure here surfaced as a Sentry
      // unhandled rejection. User-triggered (zero-price checkout): notify.
      this.log.error('confermaPrezzo0 failed', e);
      const detail = e?.responseJSON?.errors?.[0]?.detail;
      this.notify.error(detail || this.intl.t('beebeeboard error'));
      return;
    }
  }

  @task({ restartable: true }) *createRiepilogo() {
    const dataRiepilogo = this.createDataObj();

    dataRiepilogo.access_token = this.session.token;

    let riepilogo;
    try {
      riepilogo = yield beebeeAjaxPost(
        `${getConfig(this).APP.urlApi}/ecommerce_freeprice`,
        this.session.token,
        { data: dataRiepilogo }
      );
    } catch (e) {
      // Backend rejects with a 400 jqXHR ("Parametro non valido") that, left
      // uncaught, surfaced as an unhandled rejection captured as an exception
      // (Sentry EMBER-SCUOLE-ECOMMERCE-2XG). Show the message and abort.
      this.log.error('createRiepilogo failed', e);
      const detail = e?.responseJSON?.errors?.[0]?.detail;
      this.notify.error(detail || this.intl.t('beebeeboard error'));
      return;
    }

    if (!riepilogo?.data) {
      this.notify.error(this.intl.t('beebeeboard error'));
      return;
    }

    if (Array.isArray(riepilogo.data?.products)) {
      for (const product of riepilogo.data.products) {
        set(
          product,
          'partecipanti',
          typeof product.partecipanti === 'string'
            ? JSON.parse(product.partecipanti)
            : product.partecipanti
        );

        set(product, 'files', []);
        const productStore = this.store.peekRecord('product', product.id);
        if (productStore) {
          const productFiles = yield productStore.files;
          productFiles.slice().forEach((file) => {
            if (file.tags) {
              const isCert = file.tags.filter((item) => item.value === 'required');
              if (isCert.length > 0) {
                // Push into the riepilogo product's files list, not back into
                // the store relationship (which would grow it on every recalc).
                product.files.push(file);
              }
            }
          });
        }
      }
    }

    if (Array.isArray(riepilogo.data?.voucher)) {
      riepilogo.data.voucher.forEach((voucher) => {
        voucher.price_pieno = parseFloat(voucher.gift.total);
      });
    }

    if (riepilogo.data?.promo_code && riepilogo.data?.promo_code !== '') {
      if (!riepilogo.data.promo_code_valid) {
        this.notify.warning(this.intl.t('promo code not valid'));
      }
    }

    riepilogo.data.allProduct = [
      ...(riepilogo.data.groups || []),
      ...(riepilogo.data.private || []),
      ...(riepilogo.data.voucher || []),
    ];
    set(this, 'riepilogo', riepilogo);
    set(this, 'step', 3);

    later(
      this,
      () => {
        const stepEl = document.getElementById('checkOutStep2');

        if (stepEl) {
          const rect = stepEl.getBoundingClientRect();
          const top = rect.top + window.scrollY;

          window.scrollTo({ top, behavior: 'smooth' });
        }
      },
      100
    );
  }

  createDataObj() {
    return buildCheckoutDataObj({
      contact: this.contact,
      gdpr: this.applicationController.get('gdprField.organization_term_options.data'),
      cart: this.applicationController.cart,
      notes: this.notePrenotazione,
      isAlbergo: this.isAlbergo,
      promoCode: this.voucherCode,
    });
  }

  _castSafeEvents(events) {
    return castSafeEvents(events);
  }

  addPartecipanteObj(g, obj) {
    addPartecipantiTo(g, obj);
  }

  pushPartecipanti(cart, listPartecipanti) {
    flattenPartecipanti(cart, listPartecipanti);
  }

  // @action required: passed unbound as @clearItems to <PaymentMethod>, which
  // calls this.args.clearItems() — without binding `this` is the frozen args
  // proxy and this.applicationController is undefined (same class as delCart).
  @action
  clearItems() {
    this.applicationController.emptyCart();
  }
}
